import { AppHeroVisual } from "@/components/brand/AppHeroVisual";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { usePostHog } from "@/analytics/usePostHog";
import {
  ArrowRight,
  BriefcaseBusiness,
  Building2,
  House,
  MapPin,
} from "lucide-react";
import { Button } from "@/components/brand/BrandButton";
import { localizePath } from "@/lib/localeRoutes";
import { useAppUrl } from "@/hooks/useAppUrl";

export const DoctorVisitsHero = () => {
  const { t, i18n } = useTranslation("doctorVisits");
  const posthog = usePostHog();
  const bookingUrl = useAppUrl();

  const locations = [
    { icon: House, label: t("locations.items.home.title") },
    { icon: Building2, label: t("locations.items.hotel.title") },
    { icon: BriefcaseBusiness, label: t("locations.items.other.title") },
  ];

  return (
    <section className="border-b border-border bg-brand-surface px-6 pb-16 pt-6 md:pb-24 md:pt-8">
      <div className="brand-container grid [&>*]:min-w-0 items-center gap-10 lg:grid-cols-2 lg:gap-14">
        <div className="max-w-3xl">
          <p className="brand-eyebrow">
            {t("hero.eyebrow")}
          </p>
          <h1 className="brand-display">
            {t("hero.title")}
          </h1>
          <p className="mt-6 max-w-2xl text-lg leading-8 text-muted-foreground sm:text-xl">
            {t("hero.description")}
          </p>

          <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center">
            <Button
              size="lg"
              className="h-12 rounded-xl px-7 text-base sm:h-14 sm:px-8"
              asChild
            >
              <a
                href={bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() =>
                  posthog?.capture("cta_clicked", {
                    location: "doctor_visits_hero",
                    cta_type: "book_visit",
                    service: "doctor_visits",
                  })
                }
              >
                {t("hero.primaryCta")}
                <ArrowRight aria-hidden="true" />
              </a>
            </Button>
            <Button
              size="lg"
              variant="ghost"
              className="h-12 justify-start rounded-xl px-4 text-base sm:h-14"
              asChild
            >
              <Link to={localizePath("/how-it-works", i18n.language)}>
                {t("hero.processLink")}
              </Link>
            </Button>
          </div>

          <p className="mt-5 max-w-xl text-sm leading-6 text-muted-foreground">
            {t("hero.microcopy")}
          </p>
        </div>

        <div className="relative mx-auto w-full max-w-xl lg:max-w-none">
          <AppHeroVisual screen="request" prioritizeArrival />
          <ul className="mt-6 flex flex-wrap gap-x-5 gap-y-3">
            {locations.map(({ icon: Icon, label }) => (
              <li key={label} className="flex items-center gap-2 text-sm text-muted-foreground">
                <Icon className="h-4 w-4 text-primary" aria-hidden="true" />{label}
              </li>
            ))}
          </ul>
          <div className="mt-5 flex items-center gap-3 px-4 text-sm font-medium text-foreground sm:px-6">
            <MapPin className="h-5 w-5 text-primary" aria-hidden="true" />
            <span>{t("locations.eyebrow")}</span>
          </div>
        </div>
      </div>
    </section>
  );
};
